import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { GlobalNavComponent } from 'ngx-global-nav';
import { CookieService } from 'ngx-shared-services';
import { Subject, fromEvent } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil, tap } from 'rxjs/operators';
import {
  AuthService,
  I18nService,
  LoaderService,
  ProjectService,
  ResourceService,
  ScenarioService,
  SidenavService,
  TimezonesService,
  TreeService,
  UserService,
} from '@service';
import { environment } from 'src/environments/environment.prod';

@Component({
  selector: 'optima-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('nav') globalNav: GlobalNavComponent;
  @ViewChild('sidenav') sidenav: MatSidenav;
  @ViewChild('searchInput') input: ElementRef;
  sidenavOpen = true;
  disabledInput = true;
  navUrl: string;
  images = [
    '../assets/svg-icons/arrow.svg',
    '../assets/svg-icons/general-info.svg',
    '../assets/images/arrow-forward.png',
    '../assets/svg-icons/info-circle.svg',
    '../assets/svg-icons/info.svg',
    '../assets/svg-icons/grid-white.svg',
    '../assets/svg-icons/delete-red.svg',
    '../assets/svg-icons/search.svg',
    '../assets/svg-icons/search-not.svg',
    '../assets/svg-icons/filter.svg',
    '../assets/svg-icons/map-pin.svg',
    '../assets/svg-icons/manage-white.svg',
    '../assets/svg-icons/edit-white.svg',
    '../assets/svg-icons/delete.svg',
    '../assets/svg-icons/spin.svg',
    '../assets/svg-icons/edit.svg',
  ];
  loaded = 0;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private translateService: TranslateService,
    private i18nService: I18nService,
    private cookieService: CookieService,
    private sidenavService: SidenavService,
    private projectService: ProjectService,
    private treeService: TreeService,
    private scenarioService: ScenarioService,
    public loaderService: LoaderService,
    private resourceService: ResourceService<string>,
    private timezoneService: TimezonesService,
    private userService: UserService,
    private authService: AuthService,
  ) {
    this.loaderService.regexEndpoints = [
      'ess-parameters',
      'run',
      'site-parameters',
      'grid-services-parameters',
      'programs',
    ];
  }

  ngOnInit(): void {
    const { defaults } = environment;
    const locale = this.cookieService.getI18NLocale() || defaults.locale;
    const brand =
      this.cookieService.getI18NBrand() || defaults.brand.toUpperCase();
    this.translateService.setDefaultLang(
      `${defaults.locale}.${defaults.brand.toUpperCase()}`,
    );

    this.i18nService.setBrand(brand);
    this.i18nService.setLocale(locale);
    this.i18nService.loadLocale();

    this.translateService
      .use(`${locale}.${brand}`)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        document.title = this.translateService.instant('APP.TITLE');
      });

    this.sidenavService.close$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => (this.sidenavOpen = false));

    this.treeService.enableFilterInput$
      .pipe(takeUntil(this.destroy$))
      .subscribe(enable => (this.disabledInput = !enable));
  }

  ngAfterViewInit(): void {
    this.userService.id = this.globalNav?.user?.user_id;

    const logo = document.querySelector('.logo') as HTMLElement;
    logo.style.cursor = 'pointer';
    logo.addEventListener('click', () => {
      this.router.navigateByUrl('/');
    });

    this.sidenavService.setSidenav(this.sidenav);

    // Wait for auth to initialize before loading projects and scenarios
    this.authService.authInitialized$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isAuthenticated => {
        if (isAuthenticated) {          
          this.projectService
            .getProjects$()
            .pipe(takeUntil(this.destroy$))
            .subscribe();
          
          this.scenarioService
            .getAllScenarios$()
            .pipe(takeUntil(this.destroy$))
            .subscribe();
        }
      });
  }

  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.projectService.sendValue(value);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getNavUrl(): void {
    this.resourceService
      .get('links/nav')
      .pipe(takeUntil(this.destroy$))
      .subscribe(navUrl => (this.navUrl = navUrl));
  }

  isLargeScreen(): boolean {
    const width =
      window.innerWidth ||
      document.documentElement.clientWidth ||
      document.body.clientWidth;
    return width > 1280;
  }

  toggleSidenav(): void {
    this.sidenavOpen = !this.sidenavOpen;
    this.sidenavService.sendToggleEvent();
  }

  load(): void {
    this.loaded++;
    if (this.images.length == this.loaded) {
      const imagesToLoad = Array.from(
        document.querySelectorAll('.image-to-load'),
      );
      imagesToLoad.forEach(image => {
        image.remove();
      });
    }
  }
}
